import { useEffect, useState } from 'react';
import { UserPlus, X, Trash2, Mail, Check, Users } from 'lucide-react';
import { api } from '../lib/api';
import { useVault } from '../../../core/lib/vault';

interface Invitee {
  id: string;
  username: string;
  email: string;
  publicKey: string;
}

interface Member {
  userId: string;
  username: string;
  email: string;
  role: string;
  roleInVault: string;
  addedAt: string;
}

export function MembersModal({ onClose }: { onClose: () => void }) {
  const currentUser = useVault((s) => s.user);
  const sealForInvitee = useVault((s) => s.sealForInvitee);
  const [invitees, setInvitees] = useState<Invitee[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function reload() {
    setError(null);
    try {
      const [i, m] = await Promise.all([api.listInvitees(), api.listMembers()]);
      setInvitees(i.invitees);
      setMembers(m.members);
    } catch (err: any) {
      setError(err?.body?.error ?? 'Could not load member list.');
    }
  }

  useEffect(() => {
    reload();
  }, []);

  async function invite(inv: Invitee) {
    setBusy(inv.id);
    setError(null);
    try {
      const wrapped = await sealForInvitee(inv.publicKey);
      await api.inviteMember(inv.id, wrapped);
      await reload();
    } catch (err: any) {
      setError(err?.body?.error ?? err?.message ?? 'Invitation failed.');
    } finally {
      setBusy(null);
    }
  }

  async function remove(m: Member) {
    if (!confirm(`Remove ${m.username} from the vault? They'll lose access to customer data.`)) return;
    setBusy(m.userId);
    setError(null);
    try {
      await api.removeMember(m.userId);
      await reload();
    } catch (err: any) {
      setError(err?.body?.error ?? err?.message ?? 'Removal failed.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
      <div className="card w-full max-w-xl max-h-[100dvh] md:max-h-[80vh] flex flex-col">
        <div className="flex items-center gap-2 border-b border-ink-800 px-4 py-3">
          <Users size={18} className="text-dragon-500" />
          <h2 className="font-semibold">Vault Members</h2>
          <button onClick={onClose} className="btn-ghost ml-auto !p-1">
            <X size={16} />
          </button>
        </div>
        <div className="overflow-y-auto">
          <section className="p-4 border-b border-ink-800">
            <h3 className="text-xs uppercase tracking-wider text-ink-400 mb-3 flex items-center gap-1">
              <Mail size={12} /> Pending invitations
            </h3>
            {invitees.length === 0 ? (
              <p className="text-xs text-ink-500">No users awaiting invitation.</p>
            ) : (
              <ul className="space-y-2">
                {invitees.map((inv) => (
                  <li
                    key={inv.id}
                    className="flex items-center gap-3 rounded border border-ink-800 px-3 py-2"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm">{inv.username}</div>
                      <div className="text-xs text-ink-500 truncate">{inv.email}</div>
                    </div>
                    <button
                      className="btn-primary"
                      onClick={() => invite(inv)}
                      disabled={busy === inv.id}
                    >
                      <UserPlus size={14} className="mr-1" />
                      {busy === inv.id ? 'Inviting…' : 'Invite'}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
          <section className="p-4">
            <h3 className="text-xs uppercase tracking-wider text-ink-400 mb-3 flex items-center gap-1">
              <Check size={12} /> Active members
            </h3>
            <ul className="space-y-2">
              {members.map((m) => {
                const isSelf = m.userId === currentUser?.id;
                return (
                  <li
                    key={m.userId}
                    className="flex items-center gap-3 rounded border border-ink-800 px-3 py-2"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm">
                        {m.username}{' '}
                        {isSelf && <span className="text-ink-500 text-xs">(you)</span>}
                      </div>
                      <div className="text-xs text-ink-500 truncate">
                        {m.email} · {m.roleInVault}
                      </div>
                    </div>
                    <span
                      className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                        m.roleInVault === 'owner'
                          ? 'text-dragon-400 border-dragon-700'
                          : m.roleInVault === 'admin'
                            ? 'text-amber-300 border-amber-800'
                            : m.roleInVault === 'editor'
                              ? 'text-ink-300 border-ink-700'
                              : 'text-ink-400 border-ink-800'
                      }`}
                      title={`Vault role: ${m.roleInVault}`}
                    >
                      {m.roleInVault}
                    </span>
                    {!isSelf && m.roleInVault !== 'owner' && (
                      <button
                        className="btn-ghost !p-1.5 text-red-400 hover:bg-red-950"
                        onClick={() => remove(m)}
                        disabled={busy === m.userId}
                        title="Remove from vault"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        </div>
        {error && (
          <div className="border-t border-red-900 bg-red-950/40 px-4 py-2 text-xs text-red-300">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
